import React, { useState } from 'react';
import { PixelTag } from '@/types/pixel-tag';
import { toast } from 'react-toastify';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Copy, Info, Code, Tag, Link, Server } from 'lucide-react';

interface PixelCodeModalProps {
    pixelTag: PixelTag | null;
    isOpen: boolean;
    toggle: () => void;
}

const BASE_URL = 'https://track.adpulse.com.br';

const generateCode = (pixelTag: PixelTag, tab: string): string => {
    const pixelUrl = `${BASE_URL}/pixel/${pixelTag.id}`;
    const postbackUrl = `${BASE_URL}/postback/${pixelTag.id}`;
    const s2sUrl = `${BASE_URL}/s2s/${pixelTag.id}`;

    if (tab === 'html') {
        return [
            `<!-- AdPulse Pixel Tag: ${pixelTag.name} -->`,
            `<img src="${pixelUrl}" width="1" height="1" style="display:none" alt="" />`,
            `<!-- End AdPulse Pixel Tag -->`,
        ].join('\n');
    }

    if (tab === 'gtm') {
        return [
            `<!-- Cole em uma tag "HTML Personalizado" no Google Tag Manager -->`,
            `<img src="${pixelUrl}?t={{Page Path}}" width="1" height="1" style="display:none" alt="" />`,
        ].join('\n');
    }

    if (tab === 'postback') {
        return `${postbackUrl}?click_id={click_id}`;
    }

    // s2s
    return `${s2sUrl}?click_id={click_id}&conversion_value={value}`;
};

const PixelCodeModal = ({ pixelTag, isOpen, toggle }: PixelCodeModalProps) => {
    const [activeTab, setActiveTab] = useState<string>('html');

    const copyToClipboard = (code: string) => {
        navigator.clipboard.writeText(code).then(() => {
            toast.success('Código copiado para a área de transferência!');
        });
    };

    if (!pixelTag) return null;

    const code = generateCode(pixelTag, activeTab);
    const isPostbackTab = activeTab === 'postback' || activeTab === 's2s';

    return (
        <Dialog open={isOpen} onOpenChange={toggle}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>Implementar Pixel Tag: {pixelTag.name}</DialogTitle>
                    <DialogDescription>
                        Escolha o tipo de implementação e copie o código abaixo.
                    </DialogDescription>
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-4">
                        <TabsTrigger value="html" className="flex items-center gap-2">
                            <Code className="h-4 w-4" />
                            HTML
                        </TabsTrigger>
                        <TabsTrigger value="gtm" className="flex items-center gap-2">
                            <Tag className="h-4 w-4" />
                            GTM
                        </TabsTrigger>
                        <TabsTrigger value="postback" className="flex items-center gap-2">
                            <Link className="h-4 w-4" />
                            Postback
                        </TabsTrigger>
                        <TabsTrigger value="s2s" className="flex items-center gap-2">
                            <Server className="h-4 w-4" />
                            S2S
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="html" className="space-y-4">
                        <Alert>
                            <Info className="h-4 w-4" />
                            <AlertDescription>
                                Cole o código abaixo na landing page, preferencialmente antes do fechamento da tag <code>&lt;/body&gt;</code>.
                            </AlertDescription>
                        </Alert>
                        <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg text-sm overflow-x-auto">
                            <code>{code}</code>
                        </pre>
                    </TabsContent>

                    <TabsContent value="gtm" className="space-y-4">
                        <Alert>
                            <Info className="h-4 w-4" />
                            <AlertDescription>
                                No Google Tag Manager, crie uma nova tag do tipo <strong>HTML Personalizado</strong> e cole o código abaixo.
                            </AlertDescription>
                        </Alert>
                        <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg text-sm overflow-x-auto">
                            <code>{code}</code>
                        </pre>
                    </TabsContent>

                    <TabsContent value="postback" className="space-y-4">
                        <Alert>
                            <Info className="h-4 w-4" />
                            <AlertDescription>
                                Configure a URL de postback abaixo na plataforma de origem do tráfego. Substitua <code>{'{click_id}'}</code> pelo valor real do clique.
                            </AlertDescription>
                        </Alert>
                        <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg text-sm overflow-x-auto break-all">
                            <code>{code}</code>
                        </pre>
                    </TabsContent>

                    <TabsContent value="s2s" className="space-y-4">
                        <Alert>
                            <Info className="h-4 w-4" />
                            <AlertDescription>
                                Para integração servidor a servidor (S2S), faça uma requisição GET para a URL abaixo com os parâmetros reais.
                            </AlertDescription>
                        </Alert>
                        <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg text-sm overflow-x-auto break-all">
                            <code>{code}</code>
                        </pre>
                    </TabsContent>
                </Tabs>

                    <div className="flex justify-end">
                        <Button
                            onClick={() => copyToClipboard(code)}
                        >
                            <Copy className="mr-2 h-4 w-4" />
                            {isPostbackTab ? 'Copiar URL' : 'Copiar código'}
                        </Button>
                    </div>
            </DialogContent>
        </Dialog>
    );
};

export default PixelCodeModal;
